import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadGuideBySlug, loadGuidesByCategory } from '../../utils/cmsContentLoader';
import { Seo } from '../shared';
import KnowledgeHubLayout from '../explore/KnowledgeHubLayout';
import DiscoverySection from '../explore/DiscoverySection';
import OfficialCitation from '../explore/OfficialCitation';
import GuideBodyText from './GuideBodyText';
import { VISA_HUB } from '../../data/knowledgeHubContent';
import { LAST_VERIFIED } from '../../data/officialSources';
import '../../styles/explore.scss';
import '../../styles/guides.scss';

const PRIMARY_GUIDE_SLUG = 'nepal-tourist-visa';

function GovernmentHubPage() {
	const [guides, setGuides] = useState([]);
	const [visaGuides, setVisaGuides] = useState([]);
	const [primaryGuide, setPrimaryGuide] = useState(null);
	const staticHub = VISA_HUB;

	useEffect(() => {
		Promise.all([
			loadGuideBySlug(PRIMARY_GUIDE_SLUG),
			loadGuidesByCategory('government'),
			loadGuidesByCategory('visa'),
		]).then(([primary, list, visaList]) => {
			setPrimaryGuide(primary);
			setGuides(list);
			setVisaGuides(visaList);
		});
	}, []);

	const hub = primaryGuide ? {
		...staticHub,
		title: primaryGuide.title,
		lead: primaryGuide.summary || staticHub.lead,
		lastVerified: primaryGuide.official_source_verified_at || staticHub.lastVerified,
		primarySource: primaryGuide.official_source_url ? {
			name: primaryGuide.official_source_name || staticHub.primarySource.name,
			url: primaryGuide.official_source_url,
		} : staticHub.primarySource,
	} : staticHub;

	const useApiBody = Boolean(primaryGuide?.body);

	return (
		<KnowledgeHubLayout
			touristSection="plan"
			touristCurrent="Visa"
			planStepId="visa"
			hideRelated
			planBookingPanel
			seo={(
				<Seo
					title="Nepal Visa & Immigration — Official Guide"
					path="/government"
					description={primaryGuide?.summary || "Complete Nepal visa guide sourced from the Department of Immigration and Nepal Tourism Board — fees, on-arrival process, extensions, eligibility, documents, and official links."}
				/>
			)}
			hero={{
				image: hub.hero,
				eyebrow: hub.eyebrow,
				title: hub.title,
				lead: primaryGuide?.summary || 'Every question a first-time visitor asks about entering Nepal — sourced from official government websites, with last-verified dates. If a rule can change, we say so and link you to the source.',
			}}
			quickFacts={staticHub.quickFacts}
			intro={{
				title: 'Research-backed immigration information',
				text: 'Fees, eligibility, and procedures below are cited from the Department of Immigration and Nepal Tourism Board.',
				body: 'Trekking permits (TIMS, national park entry, restricted areas) are separate from your tourist visa — see the permits section at the bottom. Never rely on third-party visa advice over the official immigration website.',
			}}
			faqs={staticHub.faqs.map((f) => ({ q: f.q, a: f.a }))}
		>
			<div className="container" style={{ marginTop: '-1rem', marginBottom: '1.5rem' }}>
				<OfficialCitation
					sourceName={hub.primarySource.name}
					sourceUrl={hub.primarySource.url}
					lastVerified={hub.lastVerified || LAST_VERIFIED}
					subjectToChange
				>
					<p className="mb-0">Primary source for visa fees, on-arrival process, extensions, and eligibility. Cross-referenced with Nepal Tourism Board and TIA Immigration Office FAQ.</p>
				</OfficialCitation>
			</div>

			{useApiBody ? (
				<DiscoverySection eyebrow="Official guide" title={primaryGuide.title}>
					<GuideBodyText text={primaryGuide.body} />
					<p className="mt-3">
						<Link to={`/guides/${primaryGuide.slug}`}>Open full guide page →</Link>
					</p>
				</DiscoverySection>
			) : (
				<DiscoverySection eyebrow="Every question answered" title="Nepal tourist visa — complete guide">
					{staticHub.sections.map((s) => (
						<div key={s.id} id={s.id} className="info-card" style={{ marginBottom: '1.25rem' }}>
							<div className="info-card__body">
								<h3 className="info-card__title">{s.question}</h3>
								<p className="info-card__text">{s.answer}</p>
								{s.table && (
									<table className="fee-table">
										<thead><tr><th>Duration</th><th>Fee</th></tr></thead>
										<tbody>
											{s.table.map((row) => (
												<tr key={row.duration}><td>{row.duration}</td><td><strong>{row.fee}</strong></td></tr>
											))}
										</tbody>
									</table>
								)}
								{s.warning && <p className="section-source-tag" style={{ color: '#8a4a12', marginTop: '0.75rem' }}>{s.warning}</p>}
								{s.source && (
									<p className="section-source-tag">
										Source: <a href={s.source.url} target="_blank" rel="noopener noreferrer">{s.source.name}</a>
									</p>
								)}
								{s.editorial && <p className="section-source-tag section-source-tag--editorial">Editorial guidance — not an official government statement</p>}
							</div>
						</div>
					))}
				</DiscoverySection>
			)}

			<DiscoverySection eyebrow="Verify yourself" title="Official government links" alt>
				<div className="official-links-grid">
					{staticHub.officialLinks.map((link) => (
						<a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="official-link">
							↗ {link.label}
						</a>
					))}
				</div>
			</DiscoverySection>

			<DiscoverySection eyebrow="Visa guides" title="Visa extensions, documents & entry" intro="Step-by-step articles for tourist visa rules and paperwork.">
				<div className="guide-grid">
					{visaGuides.map((g) => (
						<Link key={g.slug} to={`/guides/${g.slug}`} className="guide-card">
							<h3 className="guide-card__title">{g.title}</h3>
							{g.summary && <p className="guide-card__summary">{g.summary}</p>}
						</Link>
					))}
				</div>
				<p className="mt-3 mb-0"><Link to="/guides" className="font-weight-bold">See all guides →</Link></p>
			</DiscoverySection>

			<DiscoverySection eyebrow="Beyond the visa" title="Permits & trekking regulations" intro="These guides cover TIMS, national park entry, restricted areas, and drone rules — all separate from your tourist visa.">
				<div className="guide-grid">
					{guides.filter((g) => g.slug !== PRIMARY_GUIDE_SLUG).map((g) => (
						<Link key={g.slug} to={`/guides/${g.slug}`} className="guide-card">
							<h3 className="guide-card__title">{g.title}</h3>
							{g.summary && <p className="guide-card__summary">{g.summary}</p>}
							{g.official_source_url ? (
								<span className="guide-card__flag" style={{ background: '#e4ebf1', color: '#2b4a68' }}>
									<i className="fas fa-university" aria-hidden="true" /> Official source cited
								</span>
							) : (
								<span className="guide-card__flag"><i className="fas fa-sync-alt" aria-hidden="true" /> Confirm before you travel</span>
							)}
						</Link>
					))}
				</div>
			</DiscoverySection>
		</KnowledgeHubLayout>
	);
}

export default GovernmentHubPage;
